// ProfilePage.tsx (no changes needed, provided for reference)
import React, { useState, useEffect } from "react";
import api from "../utils/api";
import styles from "./ProfilePage.module.scss";
import TabbedFormLayout from "../components/TabbedFormLayout/TabbedFormLayout";
import CustomInput from "../components/Inputs/CustomInput";
import CustomSelect from "../components/CustomSelect/CustomSelect";
import CustomCheckbox from "../components/CustomCheckbox/CustomCheckbox";
import ProfilePhotoUploader from "./ProfilePhotoUploader";
import { useAuth } from "../context/AuthContext";

export default function ProfilePage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  const [title, setTitle] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [languagePreference, setLanguagePreference] = useState("en");
  const [timezone, setTimezone] = useState("");

  const [personalAddress1, setPersonalAddress1] = useState("");
  const [personalAddress2, setPersonalAddress2] = useState("");
  const [personalUnit, setPersonalUnit] = useState("");
  const [personalState, setPersonalState] = useState("");
  const [personalPostal, setPersonalPostal] = useState("");

  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorType, setTwoFactorType] = useState("");
  const [securityPin, setSecurityPin] = useState("");

  useEffect(() => {
    if (user?.id) fetchUserData(user.id);
  }, [user]);

  const fetchUserData = async (userId: string) => {
    try {
      const [settingsRes, authRes] = await Promise.all([
        api.post("/core/settings/get", { userId }),
        api.get("/auth/me"),
      ]);

      const settings = settingsRes.data || {};
      const auth = authRes.data || {};

      setTitle(settings.title || "");
      setPhoneNumber(auth.phone_number || settings.phone_number || "");
      setLanguagePreference(settings.language_preference || "en");
      setTimezone(settings.timezone || "");

      setPersonalAddress1(settings.personal_address_1 || "");
      setPersonalAddress2(settings.personal_address_2 || "");
      setPersonalUnit(settings.personal_unit || "");
      setPersonalState(settings.personal_state || "");
      setPersonalPostal(settings.personal_postal || "");

      setRecoveryEmail(auth.recovery_email || "");
      setTwoFactorEnabled(auth.two_factor_enabled || false);
      setTwoFactorType(auth.two_factor_type || "");
      setSecurityPin(auth.security_pin || "");
    } catch (err) {
      console.error("Failed to fetch user profile:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user?.id) return;

    try {
      await Promise.all([
        api.post("/core/settings/update", {
          userid: user.id,
          title,
          phone_number: phoneNumber,
          personal_address_1: personalAddress1,
          personal_address_2: personalAddress2,
          personal_unit: personalUnit,
          personal_state: personalState,
          personal_postal: personalPostal,
          language_preference: languagePreference,
          timezone,
        }),
        api.post("/core/auth/update", {
          user_id: user.id,
          phone_number: phoneNumber,
          recovery_email: recoveryEmail,
          two_factor_enabled: twoFactorEnabled,
          two_factor_type: twoFactorType || null,
          security_pin: securityPin,
        }),
      ]);
      alert("Profile updated successfully.");
    } catch (err) {
      console.error("Update failed:", err);
    }
  };

  const tabs = [
    {
      label: "General Info",
      content: (
        <div className={styles.formGrid}>
          {user?.firstname && (
            <div className={styles.profileHeader}>
              <ProfilePhotoUploader userId={user?.id || ""} />
              <div className={styles.profileText}>
                <h2>{user.firstname} {user.lastname}</h2>
                <p>{user.email}</p>
              </div>
            </div>
          )}
          <CustomInput label="Title" value={title} onChange={setTitle} />
          <CustomInput label="Phone Number" value={phoneNumber} onChange={setPhoneNumber} />
          <CustomSelect label="Language" value={languagePreference} onChange={setLanguagePreference} type="language" />
          <CustomSelect label="Timezone" value={timezone} onChange={setTimezone} type="timezone" />
        </div>
      ),
    },
    {
      label: "Authentication",
      content: (
        <div className={styles.formGrid}>
          <CustomInput label="Recovery Email" value={recoveryEmail} onChange={setRecoveryEmail} />
          <CustomCheckbox label="2FA Enabled" checked={twoFactorEnabled} onChange={setTwoFactorEnabled} id="2fa_enabled" />
          <CustomSelect
            label="2FA Type"
            value={twoFactorType}
            onChange={setTwoFactorType}
            options={[
              { value: "email", label: "Email" },
              { value: "sms", label: "SMS" },
              { value: "app", label: "Authenticator App" },
            ]}
          />
          <CustomInput label="Security PIN" value={securityPin} onChange={setSecurityPin} type="password" />
        </div>
      ),
    },
    {
      label: "Address",
      content: (
        <div className={styles.formGrid}>
          <CustomInput label="Address 1" value={personalAddress1} onChange={setPersonalAddress1} />
          <CustomInput label="Address 2" value={personalAddress2} onChange={setPersonalAddress2} />
          <CustomInput label="Unit" value={personalUnit} onChange={setPersonalUnit} />
          <CustomSelect label="State" value={personalState} onChange={setPersonalState} type="state" />
          <CustomInput label="Postal Code" value={personalPostal} onChange={setPersonalPostal} />
        </div>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      {!loading && <TabbedFormLayout tabs={tabs} onSave={handleSave} />}
    </div>
  );
}